import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty, downloadAuthorized } from './client';
import { keys } from './keys';
import { BuildersListSchema } from './schemas';
import { stableReportQueryOptions } from './spend';

export function useBuilders() {
  return useQuery({
    queryKey: keys.builders(),
    queryFn: () => apiFetch('/api/builders', BuildersListSchema),
    ...stableReportQueryOptions,
  });
}

const CreateBuilderResponseSchema = z
  .object({
    id: z.coerce.number(),
    name: z.string(),
    sage_customer_id: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .passthrough();

export type CreateBuilderInput = {
  name: string;
  sage_customer_id?: string | null;
  notes?: string | null;
  entity_id?: number | null;
};

export function useCreateBuilder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBuilderInput) =>
      apiFetch('/api/builders', CreateBuilderResponseSchema, {
        method: 'POST',
        body: JSON.stringify({
          name: input.name,
          sage_customer_id: input.sage_customer_id ?? null,
          notes: input.notes ?? null,
          entity_id: input.entity_id ?? null,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.builders() }),
  });
}

/** Download per-builder Excel export (GET /api/builders/:id/export). */
export async function downloadBuilderExport(builderId: number) {
  await downloadAuthorized(`/api/builders/${builderId}/export`, `builder-${builderId}.xlsx`);
}

export function useDeleteBuilder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/builders/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.builders() }),
  });
}

export function useUpdateBuilder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      name: string;
      notes?: string | null;
      sage_customer_id?: string | null;
      entity_id?: number | null;
    }) =>
      apiFetchEmpty(`/api/builders/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: vars.name,
          notes: vars.notes ?? null,
          sage_customer_id: vars.sage_customer_id ?? null,
          entity_id: vars.entity_id ?? null,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.builders() }),
  });
}
